import { useState, useEffect, useCallback, useRef } from 'react';
import { getItemStockStatus } from '../../../../../../crud/menu_api/items';

export interface ItemStockInfo {
  item_name: string;
  missing_ingredients?: string[] | null;
  low_stock_ingredients?: string[];
}

export interface StockStatusData {
  shortages: { [item_id: string]: ItemStockInfo };
  warnings: { [item_id: string]: ItemStockInfo };
}

interface UseStockStatusResult {
  checkedAt: number;
  stockStatus: StockStatusData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getItemStockInfo: (itemId: number) => {
    hasShortage: boolean;
    hasWarning: boolean;
    shortageInfo?: ItemStockInfo;
    warningInfo?: ItemStockInfo;
  };
}

/**
 * Custom hook to fetch and manage stock status for items in a local
 * 
 * Features:
 * - Automatic fetching on mount
 * - 30-second cache aligned with backend cache
 * - Refetch capability
 * - Helper function to get stock info for specific items
 * - Error handling with user-friendly messages
 */
export const useStockStatus = (localId: number | null, strict = false): UseStockStatusResult => {
  const [stockStatus, setStockStatus] = useState<StockStatusData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const lastFetchTime = useRef<number>(0);
  const epoch = useRef(0);
  const cacheTimeout = 30000; // 30 seconds to match backend cache

  const fetchStockStatus = useCallback(async () => {
    const requestEpoch = epoch.current;
    if (!localId) {
      setStockStatus(null);
      setError(null);
      return;
    }

    // Check if cache is still valid (within 30 seconds)
    const now = Date.now();
    if (now - lastFetchTime.current < cacheTimeout && stockStatus !== null) {
      return; // Use cached data
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getItemStockStatus(localId);
      if (strict && requestEpoch !== epoch.current) return;
      if (strict && (!response?.data || typeof response.data.shortages !== 'object'
          || response.data.shortages === null || Array.isArray(response.data.shortages)
          || typeof response.data.warnings !== 'object' || response.data.warnings === null
          || Array.isArray(response.data.warnings))) {
        throw new Error('Disponibilidad de inventario desconocida');
      }
      
      if (response.data) {
        setStockStatus({
          shortages: response.data.shortages || {},
          warnings: response.data.warnings || {},
        });
        lastFetchTime.current = now;
      } else {
        throw new Error('Invalid response format from stock status API');
      }
    } catch (err: any) {
      if (strict && requestEpoch !== epoch.current) return;
      console.error('Error fetching stock status:', err);
      setError(err.message || 'Error al obtener estado de inventario');
      // Set empty stock status on error to prevent crashes
      setStockStatus(strict ? null : { shortages: {}, warnings: {} });
    } finally {
      if (!strict || requestEpoch === epoch.current) setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localId, cacheTimeout, strict]);

  // Fetch on mount and when localId changes
  useEffect(() => {
    if (strict) lastFetchTime.current = 0;
    fetchStockStatus();
    return () => { epoch.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localId, strict]);

  // Manual refetch function
  const refetch = useCallback(async () => {
    lastFetchTime.current = 0; // Force cache invalidation
    await fetchStockStatus();
  }, [fetchStockStatus]);

  /**
   * Get stock information for a specific item
   * @param itemId - The item ID to check
   * @returns Object with shortage and warning flags plus detailed info
   */
  const getItemStockInfo = useCallback((itemId: number) => {
    if (!stockStatus) {
      return {
        hasShortage: false,
        hasWarning: false,
      };
    }

    const itemIdStr = String(itemId);
    const hasShortage = itemIdStr in stockStatus.shortages;
    const hasWarning = itemIdStr in stockStatus.warnings;

    return {
      hasShortage,
      hasWarning,
      shortageInfo: hasShortage ? stockStatus.shortages[itemIdStr] : undefined,
      warningInfo: hasWarning ? stockStatus.warnings[itemIdStr] : undefined,
    };
  }, [stockStatus]);

  return {
    stockStatus,
    checkedAt: lastFetchTime.current,
    isLoading,
    error,
    refetch,
    getItemStockInfo,
  };
};

export default useStockStatus;
